import Dexie, { type Table } from "dexie";
import type {
  Client,
  ClientRate,
  FoodItem,
  FoodLog,
  LocationRule,
  MealPreset,
  MedicationLog,
  Project,
  Setting,
  SymptomLog,
  SyncEntityName,
  SyncMutation,
  SyncTableEntity,
  TranscriptionJob,
  VoiceNote,
  WorkSession
} from "./types";

export class TrackerDatabase extends Dexie {
  clients!: Table<Client, string>;
  client_rates!: Table<ClientRate, string>;
  projects!: Table<Project, string>;
  work_sessions!: Table<WorkSession, string>;
  food_items!: Table<FoodItem, string>;
  food_logs!: Table<FoodLog, string>;
  meal_presets!: Table<MealPreset, string>;
  medication_logs!: Table<MedicationLog, string>;
  symptom_logs!: Table<SymptomLog, string>;
  location_rules!: Table<LocationRule, string>;
  voice_notes!: Table<VoiceNote, string>;
  transcription_jobs!: Table<TranscriptionJob, string>;
  sync_mutations!: Table<SyncMutation, string>;
  settings!: Table<Setting, string>;

  constructor() {
    super("tracker_quotidiano");
    this.version(1).stores({
      clients: "id, name, sync_status, server_rev, updated_at, deleted_at",
      projects: "id, client_id, name, archived, sync_status, server_rev, updated_at, deleted_at",
      work_sessions:
        "id, client_id, project_id, started_at, ended_at, billable, sync_status, server_rev, updated_at, deleted_at",
      food_items: "id, name, use_count, last_used_at, sync_status, server_rev, updated_at, deleted_at",
      food_logs: "id, food_item_id, eaten_at, sync_status, server_rev, updated_at, deleted_at",
      medication_logs: "id, date, medication, sync_status, server_rev, updated_at, deleted_at",
      symptom_logs: "id, occurred_at, felt_sick, vomited, sync_status, server_rev, updated_at, deleted_at",
      location_rules: "id, client_id, label, last_confirmed_at, sync_status, server_rev, updated_at, deleted_at",
      voice_notes:
        "id, created_at, upload_status, transcription_status, sync_status, server_rev, updated_at, deleted_at",
      transcription_jobs: "id, voice_note_id, status, sync_status, server_rev, updated_at, deleted_at",
      sync_mutations: "id, entity, entity_id, created_at, pushed_at",
      settings: "key"
    });
    this.version(2).stores({
      clients: "id, name, sync_status, server_rev, updated_at, deleted_at",
      client_rates:
        "id, client_id, valid_from, [client_id+valid_from], sync_status, server_rev, updated_at, deleted_at",
      projects: "id, client_id, name, archived, sync_status, server_rev, updated_at, deleted_at",
      work_sessions:
        "id, client_id, project_id, started_at, ended_at, billable, sync_status, server_rev, updated_at, deleted_at",
      food_items: "id, name, use_count, last_used_at, sync_status, server_rev, updated_at, deleted_at",
      food_logs: "id, food_item_id, eaten_at, sync_status, server_rev, updated_at, deleted_at",
      medication_logs: "id, date, medication, sync_status, server_rev, updated_at, deleted_at",
      symptom_logs: "id, occurred_at, felt_sick, vomited, sync_status, server_rev, updated_at, deleted_at",
      location_rules: "id, client_id, label, last_confirmed_at, sync_status, server_rev, updated_at, deleted_at",
      voice_notes:
        "id, created_at, upload_status, transcription_status, sync_status, server_rev, updated_at, deleted_at",
      transcription_jobs: "id, voice_note_id, status, sync_status, server_rev, updated_at, deleted_at",
      sync_mutations: "id, entity, entity_id, created_at, pushed_at",
      settings: "key"
    });
    this.version(3).stores({
      clients: "id, name, sync_status, server_rev, updated_at, deleted_at",
      client_rates:
        "id, client_id, valid_from, [client_id+valid_from], sync_status, server_rev, updated_at, deleted_at",
      projects: "id, client_id, name, archived, sync_status, server_rev, updated_at, deleted_at",
      work_sessions:
        "id, client_id, project_id, started_at, ended_at, billable, sync_status, server_rev, updated_at, deleted_at",
      food_items: "id, name, use_count, last_used_at, sync_status, server_rev, updated_at, deleted_at",
      food_logs: "id, food_item_id, meal_id, eaten_at, sync_status, server_rev, updated_at, deleted_at",
      medication_logs: "id, date, medication, sync_status, server_rev, updated_at, deleted_at",
      symptom_logs: "id, occurred_at, felt_sick, vomited, sync_status, server_rev, updated_at, deleted_at",
      location_rules: "id, client_id, label, last_confirmed_at, sync_status, server_rev, updated_at, deleted_at",
      voice_notes:
        "id, created_at, upload_status, transcription_status, sync_status, server_rev, updated_at, deleted_at",
      transcription_jobs: "id, voice_note_id, status, sync_status, server_rev, updated_at, deleted_at",
      sync_mutations: "id, entity, entity_id, created_at, pushed_at",
      settings: "key"
    });
    this.version(4).stores({
      clients: "id, name, sync_status, server_rev, updated_at, deleted_at",
      client_rates:
        "id, client_id, valid_from, [client_id+valid_from], sync_status, server_rev, updated_at, deleted_at",
      projects: "id, client_id, name, archived, sync_status, server_rev, updated_at, deleted_at",
      work_sessions:
        "id, client_id, project_id, started_at, ended_at, billable, sync_status, server_rev, updated_at, deleted_at",
      food_items: "id, name, use_count, last_used_at, sync_status, server_rev, updated_at, deleted_at",
      food_logs: "id, food_item_id, meal_id, eaten_at, sync_status, server_rev, updated_at, deleted_at",
      meal_presets: "id, slot, sync_status, server_rev, updated_at, deleted_at",
      medication_logs: "id, date, medication, sync_status, server_rev, updated_at, deleted_at",
      symptom_logs: "id, occurred_at, felt_sick, vomited, sync_status, server_rev, updated_at, deleted_at",
      location_rules: "id, client_id, label, last_confirmed_at, sync_status, server_rev, updated_at, deleted_at",
      voice_notes:
        "id, created_at, upload_status, transcription_status, sync_status, server_rev, updated_at, deleted_at",
      transcription_jobs: "id, voice_note_id, status, sync_status, server_rev, updated_at, deleted_at",
      sync_mutations: "id, entity, entity_id, created_at, pushed_at",
      settings: "key"
    });
    this.version(5)
      .stores({
        symptom_logs: "id, occurred_at, felt_sick, vomited, symptom, sync_status, server_rev, updated_at, deleted_at"
      })
      .upgrade(async (tx) => {
        // Farmaci: il pantoprazolo legacy diventa la prima voce della categoria IPP.
        await tx
          .table<MedicationLog>("medication_logs")
          .toCollection()
          .modify((log) => {
            if ((log.medication as string) === "pantoprazolo") {
              log.medication = "ipp";
            }
          });
      });
  }
}

export const db = new TrackerDatabase();

export function tableFor(entity: SyncEntityName): Table<SyncTableEntity, string> {
  return db.table(entity) as Table<SyncTableEntity, string>;
}

export function createId(): string {
  const browserCrypto = globalThis.crypto;
  if (browserCrypto?.randomUUID) {
    return browserCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (browserCrypto?.getRandomValues) {
    browserCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex
    .slice(8, 10)
    .join("")}-${hex.slice(10, 16).join("")}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function todayKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function emitDbChange(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new CustomEvent("tracker-db-changed"));
}

export async function getSetting(key: string, fallback = ""): Promise<string> {
  const setting = await db.settings.get(key);
  return setting?.value ?? fallback;
}

export async function queueMutation(
  entity: SyncEntityName,
  entityId: string,
  op: "upsert" | "delete",
  payload: unknown
): Promise<void> {
  await db.sync_mutations.put({
    id: createId(),
    entity,
    entity_id: entityId,
    op,
    payload,
    created_at: nowIso(),
    pushed_at: null
  });
  emitSyncNeeded();
}

export async function saveEntity<T extends SyncTableEntity>(
  entity: SyncEntityName,
  value: T,
  op: "upsert" | "delete" = "upsert"
): Promise<void> {
  const table = tableFor(entity) as Table<T, string>;
  await table.put(value);
  await queueMutation(entity, value.id, op, stripLocalOnlyFields(value));
  emitDbChange();
}

export function stripLocalOnlyFields(value: unknown): unknown {
  if (!value || typeof value !== "object") {
    return value;
  }
  const clone = { ...(value as Record<string, unknown>) };
  delete clone.audio_blob;
  return clone;
}

export function makeBase() {
  const timestamp = nowIso();
  return {
    id: createId(),
    created_at: timestamp,
    updated_at: timestamp,
    deleted_at: null,
    server_rev: null,
    sync_status: "pending" as const
  };
}

export async function pendingMutationCount(): Promise<number> {
  return db.sync_mutations.filter((mutation) => !mutation.pushed_at).count();
}

function emitSyncNeeded(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new CustomEvent("tracker-sync-needed"));
}
